import {
  faLayerGroup,
  faMagnifyingGlass,
  faPlus,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import {
  useDeleteLibraryQuestion,
  useLibraryQuestions,
  useLibrarySections
} from '@/hooks/useFormLibrary';
import { Button, Input, Modal, TabSwitch, TabSwitchList, TabSwitchTab } from '@/ui';

import type { FormBuilderWidget, LibraryQuestion, LibrarySection } from '../types';

import { badgeClasses, FIELD_TYPES, fieldTypeMeta } from './fieldTypes';

const TABS = ['fields', 'questions', 'sections'] as const;

type Tab = (typeof TABS)[number];

interface LibraryPanelProps {
  onAddField: (widget: FormBuilderWidget) => void;
  onInsertQuestion: (question: LibraryQuestion) => void;
  onInsertSection: (section: LibrarySection) => void;
  // Keys already on the form. A linked question keeps its own key, so one
  // that's taken can't be inserted again.
  usedKeys: Set<string>;
  // The section fields and questions currently land in, if any. Named so the
  // panel can say where a click will put things rather than leaving it a mode.
  targetSectionTitle?: string;
  onClearTarget: () => void;
}

const Badge = ({ widget }: { widget: FormBuilderWidget }) => (
  <span
    className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide ${badgeClasses(widget)}`}>
    {fieldTypeMeta(widget).badge}
  </span>
);

const LibraryPanel = ({
  onAddField,
  onInsertQuestion,
  onInsertSection,
  usedKeys,
  targetSectionTitle,
  onClearTarget
}: LibraryPanelProps) => {
  const [tab, setTab] = useState<Tab>('fields');
  const [search, setSearch] = useState('');
  const [questionToDelete, setQuestionToDelete] = useState<LibraryQuestion | null>(null);

  const questions = useLibraryQuestions(search, tab === 'questions');
  const sections = useLibrarySections(search, tab === 'sections');
  const deleteQuestion = useDeleteLibraryQuestion();
  const fieldTypes = FIELD_TYPES.filter((meta) =>
    meta.label.toLowerCase().includes(search.trim().toLowerCase())
  );

  const placeholder =
    tab === 'fields'
      ? 'Search field types…'
      : tab === 'questions'
        ? 'Search questions…'
        : 'Search sections…';

  return (
    <div className="flex min-h-0 flex-col border-r border-uniform-gray-100 bg-uniform-gray-50 dark:border-uniform-gray-700 dark:bg-uniform-gray-900">
      <div className="px-[18px] pb-3.5 pt-[18px]">
        <div className="mb-3 flex items-center gap-2">
          <FontAwesomeIcon
            icon={faLayerGroup}
            className="text-uniform-gray-500 dark:text-uniform-gray-400"
          />
          <span className="text-[13.5px] font-bold text-uniform-gray-900 dark:text-uniform-gray-100">
            Library
          </span>
        </div>
        <TabSwitch
          variant="pills"
          size="sm"
          stretch
          selectedIndex={TABS.indexOf(tab)}
          onChange={(index) => setTab(TABS[index])}>
          <TabSwitchList>
            {TABS.map((value) => (
              <TabSwitchTab key={value} className="capitalize">
                {value}
              </TabSwitchTab>
            ))}
          </TabSwitchList>
        </TabSwitch>
        <Input
          id="library-search"
          type="search"
          label={placeholder}
          hideLabel
          className="mt-3"
          leadingIcon={<FontAwesomeIcon icon={faMagnifyingGlass} className="text-xs" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
        />
        {/* Where a click lands is otherwise invisible, so the target is named
            with the way out next to it. Sections always go in the form body. */}
        {targetSectionTitle && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-2 dark:border-blue-800 dark:bg-blue-950/30">
            <FontAwesomeIcon
              icon={faLayerGroup}
              className="text-[11px] text-blue-600 dark:text-blue-300"
            />
            <span className="min-w-0 flex-1 truncate text-[11.5px] leading-snug text-blue-700 dark:text-blue-300">
              Adding to <b>{targetSectionTitle}</b>
            </span>
            <Button
              size="xs"
              variant="ghost"
              color="neutral"
              aria-label="Add to the form body instead"
              onClick={onClearTarget}>
              Form body
            </Button>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-[18px]">
        {tab === 'fields' && (
          <div>
            <div className="flex items-center justify-between px-1 pb-2 pt-0.5">
              <span className="text-[11px] font-bold tracking-wider text-uniform-gray-400 dark:text-uniform-gray-500">
                FIELD TYPES
              </span>
              <span className="text-[11px] text-uniform-gray-400 dark:text-uniform-gray-500">
                {fieldTypes.length}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {fieldTypes.map((meta) => (
                <Button
                  key={meta.widget}
                  variant="outline"
                  color="neutral"
                  onClick={() => onAddField(meta.widget)}
                  className="h-auto justify-start whitespace-normal rounded-xl border-uniform-gray-200 bg-white px-[11px] py-2.5 text-left hover:border-blue-300 dark:border-uniform-gray-700 dark:bg-uniform-gray-800 dark:hover:border-blue-600">
                  <span className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-md bg-uniform-gray-100 text-uniform-gray-500 dark:bg-uniform-gray-700 dark:text-uniform-gray-400">
                    <FontAwesomeIcon icon={faPlus} className="text-[11px]" />
                  </span>
                  <span className="text-[12.5px] font-semibold leading-tight text-uniform-gray-800 dark:text-uniform-gray-200">
                    {meta.label}
                  </span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {tab === 'questions' && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between px-1 pb-2 pt-0.5">
              <span className="text-[11px] font-bold tracking-wider text-uniform-gray-400 dark:text-uniform-gray-500">
                REUSABLE QUESTIONS
              </span>
              <span className="text-[11px] text-uniform-gray-400 dark:text-uniform-gray-500">
                {questions.data?.length ?? 0}
              </span>
            </div>
            {questions.isPending && (
              <p className="px-1 text-[12px] text-uniform-gray-400 dark:text-uniform-gray-500">
                Loading…
              </p>
            )}
            {questions.data?.map((question) => {
              const onForm = usedKeys.has(question.key);
              return (
                <div
                  key={question.id}
                  className="group rounded-xl border border-uniform-gray-200 bg-white p-3 dark:border-uniform-gray-700 dark:bg-uniform-gray-800">
                  <div className="flex items-start gap-2.5">
                    <div className="min-w-0 flex-1">
                      <Badge widget={question.definition['x-ui'].widget} />
                      <div className="mt-1 text-[13.5px] font-semibold leading-tight text-uniform-gray-900 dark:text-uniform-gray-100">
                        {question.text || question.key}
                      </div>
                      <div className="mt-1 text-[11px] text-uniform-gray-400 dark:text-uniform-gray-500">
                        {onForm ? 'Already on this form' : question.key}
                      </div>
                    </div>
                    <div
                      className={`flex flex-none items-center gap-1 transition ${
                        onForm
                          ? 'opacity-100'
                          : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100'
                      }`}>
                      <Button
                        size="icon-sm"
                        variant="subtle"
                        color="secondary"
                        disabled={onForm}
                        title={onForm ? 'Already on this form' : 'Insert linked to the library'}
                        aria-label={`Insert ${question.text || question.key}`}
                        onClick={() => onInsertQuestion(question)}>
                        <FontAwesomeIcon icon={faPlus} className="text-xs" />
                      </Button>
                      <Button
                        size="icon-sm"
                        variant="subtle"
                        color="neutral"
                        title="Remove from the library"
                        aria-label={`Remove ${question.text || question.key} from the library`}
                        onClick={() => setQuestionToDelete(question)}>
                        <FontAwesomeIcon icon={faTrash} className="text-xs" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            <p className="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
              Inserted questions stay <b>linked</b> — they keep the library key and answers roll up
              across every form asking them. Unstar a question to give this form its own copy.
            </p>
          </div>
        )}

        {tab === 'sections' && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between px-1 pb-2 pt-0.5">
              <span className="text-[11px] font-bold tracking-wider text-uniform-gray-400 dark:text-uniform-gray-500">
                REUSABLE SECTIONS
              </span>
              <span className="text-[11px] text-uniform-gray-400 dark:text-uniform-gray-500">
                {sections.data?.length ?? 0}
              </span>
            </div>
            {sections.isPending && (
              <p className="px-1 text-[12px] text-uniform-gray-400 dark:text-uniform-gray-500">
                Loading…
              </p>
            )}
            {sections.data?.map((section) => {
              const clash = section.questions.find((member) => usedKeys.has(member.key));
              return (
                <div
                  key={section.id}
                  className="group rounded-xl border border-uniform-gray-200 bg-white p-3 dark:border-uniform-gray-700 dark:bg-uniform-gray-800">
                  <div className="flex items-start gap-2.5">
                    <div className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg bg-uniform-gray-100 text-uniform-gray-500 dark:bg-uniform-gray-700 dark:text-uniform-gray-400">
                      <FontAwesomeIcon icon={faLayerGroup} className="text-xs" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-bold text-uniform-gray-900 dark:text-uniform-gray-100">
                        {section.title || section.key}
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-uniform-gray-500 dark:text-uniform-gray-400">
                        {clash
                          ? `${clash.key} is already on this form`
                          : `${section.questions.length} questions`}
                      </div>
                    </div>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="secondary"
                      disabled={!!clash}
                      aria-label={`Insert section ${section.title || section.key}`}
                      onClick={() => onInsertSection(section)}
                      className={`flex-none transition ${
                        clash
                          ? 'opacity-100'
                          : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100'
                      }`}>
                      <FontAwesomeIcon icon={faPlus} className="text-[11px]" />
                      Insert
                    </Button>
                  </div>
                </div>
              );
            })}
            <p className="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
              Inserted sections stay <b>linked</b> — updating the master pushes changes to every
              form. Unlink a section to customize it here.
            </p>
          </div>
        )}
      </div>

      {questionToDelete && (
        <Modal
          open
          onClose={() => setQuestionToDelete(null)}
          title={`Remove "${questionToDelete.text || questionToDelete.key}" from the library?`}
          primaryAction={{
            label: 'Remove',
            onClick: () => {
              deleteQuestion.mutate(questionToDelete.id, {
                onSuccess: () => setQuestionToDelete(null)
              });
            }
          }}
          secondaryAction={{ label: 'Cancel', onClick: () => setQuestionToDelete(null) }}>
          <p className="text-[13px] text-uniform-gray-600 dark:text-uniform-gray-400">
            Forms that already asked this question keep their answers — this only takes it out of
            the picker for new forms.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default LibraryPanel;
